import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { faqs } from '@/mocks/products';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const allFaqsForSchema = [
      ...faqs,
      {
        question: 'Where are Faasley products available for purchase?',
        answer: 'Faasley products are available online through our website www.faasley.com. We are also expanding to major e-commerce platforms and will soon be available at select retail outlets across India.',
      },
      {
        question: 'Do you offer customized gifting solutions?',
        answer: 'Yes! We offer customized gift hampers featuring our premium products. Perfect for festivals, corporate gifting, and special occasions. Contact us for custom packaging and bulk gifting requirements.',
      },
      {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major payment methods including Credit/Debit cards, Net Banking, UPI (GPay, PhonePe, Paytm), and Cash on Delivery in select locations.',
      },
      {
        question: 'How can I track my order?',
        answer: 'Once your order is shipped, you will receive a tracking number via email and SMS. You can use this number to track your shipment on our website or the courier partner\'s website.',
      },
    ];

    const schemaData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/faq#webpage`,
          url: `${SITE_URL}/faq`,
          name: 'FAQ | Faasley Foods — Frequently Asked Questions',
          description: 'Find answers to frequently asked questions about Faasley Foods products, shipping, orders, payments, and investment opportunities.',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${SITE_URL}/faq` },
            ],
          },
        },
        {
          '@type': 'FAQPage',
          name: 'Faasley Foods FAQ',
          url: `${SITE_URL}/faq`,
          mainEntity: allFaqsForSchema.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-faq';
    script.textContent = JSON.stringify(schemaData);
    document.head.appendChild(script);
    document.title = 'FAQ | Faasley Foods — Frequently Asked Questions';
    return () => {
      const el = document.getElementById('schema-faq');
      if (el) el.remove();
    };
  }, []);

  const additionalFaqs = [
    {
      question: 'Where are Faasley products available for purchase?',
      answer: 'Faasley products are available online through our website www.faasley.com. We are also expanding to major e-commerce platforms and will soon be available at select retail outlets across India.',
    },
    {
      question: 'Do you offer customized gifting solutions?',
      answer: 'Yes! We offer customized gift hampers featuring our premium products. Perfect for festivals, corporate gifting, and special occasions. Contact us for custom packaging and bulk gifting requirements.',
    },
    {
      question: 'What payment methods do you accept?',
      answer: 'We accept all major payment methods including Credit/Debit cards, Net Banking, UPI (GPay, PhonePe, Paytm), and Cash on Delivery in select locations.',
    },
    {
      question: 'How can I track my order?',
      answer: 'Once your order is shipped, you will receive a tracking number via email and SMS. You can use this number to track your shipment on our website or the courier partner\'s website.',
    },
  ];

  const allFaqs = [...faqs, ...additionalFaqs];

  const faqCategories = [
    { id: 'all', label: 'All Questions' },
    { id: 'products', label: 'Products' },
    { id: 'shipping', label: 'Shipping & Delivery' },
    { id: 'orders', label: 'Orders & Payment' },
    { id: 'investment', label: 'Investment' },
  ];

  const filteredFaqs = allFaqs.filter((faq) =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-cream dark:bg-gray-900 transition-colors duration-300">
      <Navbar />

      {/* Hero */}
      <div className="pt-[240px] md:pt-[260px]">
        <div className="h-[280px] md:h-[350px] relative overflow-hidden bg-forest">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 5 L55 55 L5 55 Z' fill='%23ffffff' fill-opacity='1'/%3E%3C/svg%3E")`,
            }}></div>
          </div>
          <div className="relative z-10 h-full flex items-center justify-center text-center px-4">
            <div>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
                Frequently Asked Questions
              </h1>
              <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
                Everything you need to know about Faasley Foods products and services
              </p>
              {/* Search */}
              <div className="relative max-w-lg mx-auto">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search questions..."
                  className="w-full bg-white/10 backdrop-blur-sm border border-white/30 rounded-full px-6 py-3 pr-12 text-white placeholder:text-white/60 focus:outline-none focus:border-white text-sm"
                />
                <i className="ri-search-line absolute right-4 top-1/2 -translate-y-1/2 text-white/60"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Content */}
      <section className="py-16 md:py-24">
        <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {faqCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-6 py-2.5 rounded-full font-medium text-sm transition-all whitespace-nowrap ${
                  selectedCategory === cat.id
                    ? 'bg-saffron text-white shadow-lg shadow-saffron/30'
                    : 'bg-white text-brown hover:bg-saffron/10'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* FAQ Accordion */}
          <div className="max-w-4xl mx-auto space-y-4">
            {filteredFaqs.map((faq, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl border transition-all duration-300 ${
                  openIndex === index
                    ? 'border-saffron shadow-md'
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full flex items-center justify-between p-5 md:p-6 text-left"
                >
                  <span className="font-semibold text-brown pr-4">{faq.question}</span>
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                      openIndex === index
                        ? 'bg-saffron text-white'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <i className={`ri-${openIndex === index ? 'subtract' : 'add'}-line`}></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}

            {filteredFaqs.length === 0 && (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-question-line text-gray-400 text-3xl"></i>
                </div>
                <h3 className="text-brown font-semibold text-lg mb-2">No results found</h3>
                <p className="text-gray-500">Try a different search term</p>
              </div>
            )}
          </div>

          {/* Contact Prompt */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="bg-forest rounded-2xl p-8 text-center text-white">
              <h3 className="font-display font-bold text-xl mb-2">Didn't find your answer?</h3>
              <p className="text-white/80 mb-6">Our team is happy to help with any questions you may have</p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-forest font-semibold px-6 py-3 rounded-full hover:bg-cream transition-colors"
              >
                <i className="ri-customer-service-line"></i>
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}